import type { BookingDTO } from '@/types/dto/agent';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown> | unknown[]>;
  }
}

export enum GTM_EVENT {
  SEARCH_INITIATED = 'search_initiated',
  OUTBOUND_JOURNEY_SELECTED = 'outbound_journey_selected',
  INBOUND_JOURNEY_SELECTED = 'inbound_journey_selected',
  OUTBOUND_OFFER_CONFIRMED = 'outbound_offer_confirmed',
  INBOUND_OFFER_CONFIRMED = 'inbound_offer_confirmed',
  SEAT_SELECTION_OPENED = 'seat_selection_opened',
  SEAT_SELECTED = 'seat_selected',
  SEAT_SELECTION_COMPLETED = 'seat_selection_completed',
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_PAYMENT_INFO = 'add_payment_info',
  PURCHASE = 'purchase',
}

export interface GTMEcommerceItem extends Record<string, unknown> {
  item_id?: string;
  item_name: string;
  price?: number;
  quantity?: number;
}

export interface GTMEcommerceData extends Record<string, unknown> {
  transaction_id?: string;
  value?: number;
  currency?: string;
  items: GTMEcommerceItem[];
}

export const mapBookingToEcommerceItems = (
  booking: BookingDTO | null
): GTMEcommerceItem[] =>
  booking?.bookedTrips?.flatMap((trip) =>
    (trip.bookedOffers ?? []).flatMap((offer) =>
      (offer.admissions ?? []).map((admission) => ({
        item_id: admission.id ?? offer.id,
        item_name:
          admission.summary ?? admission.products?.[0]?.description ?? 'Ticket',
        price: admission.price?.amount,
        quantity: 1,
      }))
    )
  ) ?? [];

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

// Consent Mode commands must be pushed as the raw arguments list — GTM reads
// `arguments`, so we forward the args array as-is rather than spreading it.
const gtag = function (...args: unknown[]) {
  window.dataLayer.push(args);
} as Gtag.Gtag;

/**
 * Sets the Google Consent Mode v2 default state. MUST be called before
 * initializeGTM() so the customer's GTM container sees "denied" before any tag
 * evaluates its consent requirement. Cookiebot later calls
 * gtag('consent', 'update', ...) to grant the categories the user opts into.
 */
export const setDefaultConsent = () => {
  ensureDataLayer();

  // GDPR-strict Consent Mode v2 baseline: deny everything except
  // security_storage. Cookiebot flips categories to granted on user opt-in.
  // wait_for_update gives a slow-loading Cookiebot time to send its update
  // before any consent-gated tag fires.
  const consentDefaults: Gtag.ConsentParams = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    // Cookiebot is loaded inside the customer's GTM container (two serial
    // network fetches before its consent update can arrive), so give it a
    // generous window before tags act on the denied defaults.
    wait_for_update: 2000,
  };

  gtag('consent', 'default', consentDefaults);
};

const getGTMScriptSelector = (containerId: string) =>
  `script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`;

const injectGTMScript = (containerId: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

export const initializeGTM = (containerId: string): boolean => {
  if (!containerId) {
    return false;
  }

  if (document.querySelector(getGTMScriptSelector(containerId))) {
    return true;
  }

  ensureDataLayer();

  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  injectGTMScript(containerId);

  return true;
};

export const isGTMInitialized = (): boolean => {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return false;
  }

  return (
    document.querySelector('script[src*="googletagmanager.com/gtm.js"]') !==
    null
  );
};

export const trackEcommerceEvent = (
  eventName: GTM_EVENT,
  ecommerce: GTMEcommerceData
) => {
  if (!isGTMInitialized()) {
    return;
  }

  // Reset before every ecommerce push so GTM's recursive data-layer merge
  // can't leak items/values from a previous ecommerce event into this one.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event: eventName, ecommerce });
};

export const trackCustomEvent = (
  eventName: string,
  eventData?: Record<string, unknown>
) => {
  if (!isGTMInitialized()) {
    return;
  }

  window.dataLayer.push({
    event: eventName,
    ...eventData,
  });
};
